import React from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { 
  UserPlus, 
  Calendar, 
  Square, 
  Link as LinkIcon, 
  Music, 
  DollarSign,
  Award,
  CheckCircle,
  Clock,
  Users,
  Headphones,
  Smartphone,
  Globe,
  ArrowRight
} from 'react-feather';

const FeaturesPage: React.FC = () => {

  const steps = [
    {
      id: 1,
      icon: <UserPlus size={48} className="text-primary mb-3" />,
      title: "Create Your DJ Account",
      description: "Sign up for free and create your professional DJ profile with your bio, genres, and experience.",
      details: [
        "Set up your profile with photos and bio",
        "Add your music genres and experience",
        "Connect your social media and streaming platforms",
        "Get verified for premium features"
      ]
    },
    {
      id: 2,
      icon: <Calendar size={48} className="text-success mb-3" />,
      title: "Create Events",
      description: "Create events for your gigs, parties, or performances with detailed information.",
      details: [
        "Add event name, location, and date",
        "Set event as active or inactive",
        "Manage multiple events simultaneously",
        "Track event statistics and requests"
      ]
    },
    {
      id: 3,
      icon: <Square size={48} className="text-warning mb-3" />,
      title: "Generate QR Code & Public Link",
      description: "Get a unique QR code and public link for each event that guests can easily access.",
      details: [
        "Automatic QR code generation for each event",
        "Public link that works on any device",
        "No app download required for guests",
        "Instant access to song request form"
      ]
    },
    {
      id: 4,
      icon: <Music size={48} className="text-info mb-3" />,
      title: "Receive Song Requests",
      description: "Guests scan the QR code or use the link to submit song requests with optional donations.",
      details: [
        "Real-time song request submissions",
        "Guest information and messages",
        "Optional donation system",
        "Request approval and management"
      ]
    }
  ];

  const features = [
    {
      icon: <LinkIcon size={32} className="text-primary" />,
      title: "Public Event Links",
      description: "Each event gets a unique public link that guests can access from any device without downloading an app."
    },
    {
      icon: <Square size={32} className="text-success" />,
      title: "QR Code Generation",
      description: "Automatically generated QR codes for each event that can be displayed at venues for easy access."
    },
    {
      icon: <DollarSign size={32} className="text-warning" />,
      title: "Donation Integration",
      description: "Allow guests to add optional donations with their song requests to support your performance."
    },
    {
      icon: <Users size={32} className="text-info" />,
      title: "Real-time Management",
      description: "View and manage incoming requests in real-time during your performance."
    },
    {
      icon: <Award size={32} className="text-danger" />,
      title: "Professional Profile",
      description: "Showcase your talent with a verified DJ profile including your bio, genres, and streaming links."
    },
    {
      icon: <Music size={32} className="text-dark" />,
      title: "Multi-Platform Support",
      description: "Embed your SoundCloud and Spotify playlists directly on your profile page."
    }
  ];

  return (
    <div className="features-page">
      {/* Hero Section */}
      <section className="hero-section py-5" style={{ background: 'linear-gradient(135deg, #6C63FF 0%, #4F46E5 100%)' }}>
        <Container>
          <Row className="text-center text-white">
            <Col>
              <h1 className="display-4 fw-bold mb-4">How MyWeeshy Works</h1>
              <p className="lead mb-4">
                Transform your DJ experience with our simple 4-step process
              </p>
              <Link to="/signup" className="text-decoration-none">
                <Button 
                  size="lg" 
                  className="px-4"
                  style={{ 
                    backgroundColor: 'white', 
                    borderColor: 'white',
                    color: '#6C63FF'
                  }}
                >
                  Get Started for Free
                  <ArrowRight size={20} className="ms-2" />
                </Button>
              </Link>
            </Col>
          </Row>
        </Container>
      </section>

      {/* How It Works Steps */}
      <section className="steps-section py-5">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-3">Simple Steps to Success</h2>
              <p className="lead text-muted">Get up and running in minutes</p>
            </Col>
          </Row>

          <Row className="g-4">
            {steps.map((step, index) => (
              <Col key={step.id} lg={6} className="mb-4">
                <Card className="h-100 border-0 shadow-sm step-card">
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-start">
                      <div 
                        className="step-number me-3 flex-shrink-0"
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#6C63FF',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold'
                        }}
                      >
                        {step.id}
                      </div>
                      <div className="flex-grow-1">
                        <div className="text-center mb-3">
                          {step.icon}
                        </div>
                        <h4 className="mb-3">{step.title}</h4>
                        <p className="text-muted mb-3">{step.description}</p>
                        <ul className="list-unstyled">
                          {step.details.map((detail, idx) => (
                            <li key={idx} className="d-flex align-items-center mb-2">
                              <CheckCircle size={16} className="text-success me-2 flex-shrink-0" />
                              <span className="small">{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Key Features */}
      <section className="features-grid py-5 bg-light">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-3">Powerful Features</h2>
              <p className="lead text-muted">Everything you need to engage your audience</p>
            </Col>
          </Row>

          <Row className="g-4">
            {features.map((feature, index) => (
              <Col key={index} md={6} lg={4}>
                <Card className="feature-card h-100 border-0 shadow-sm text-center p-4">
                  <Card.Body>
                    <div className="mb-3">
                      {feature.icon}
                    </div>
                    <h5 className="mb-3">{feature.title}</h5>
                    <p className="text-muted mb-0">{feature.description}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Use Case Example */}
      <section className="use-case py-5">
        <Container>
          <Row className="align-items-center">
            <Col lg={6}>
              <h2 className="display-6 fw-bold mb-4">See It In Action</h2>
              <div className="scenario mb-4">
                <h4 className="text-primary mb-3">📍 At Your Event</h4>
                <div className="steps-flow">
                  <div className="step-flow-item mb-3 p-3 bg-light rounded">
                    <strong>1. Display QR Code:</strong> Show the QR code on screens or print it on tables
                  </div>
                  <div className="step-flow-item mb-3 p-3 bg-light rounded">
                    <strong>2. Guests Scan:</strong> Guests scan with their phone camera - no app needed
                  </div>
                  <div className="step-flow-item mb-3 p-3 bg-light rounded">
                    <strong>3. Submit Requests:</strong> They fill out song name, their name, and optional donation
                  </div>
                  <div className="step-flow-item mb-3 p-3 bg-light rounded">
                    <strong>4. You Receive:</strong> See all requests in real-time on your dashboard
                  </div>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div className="text-center">
                <div 
                  className="qr-demo p-4 rounded shadow-sm"
                  style={{ backgroundColor: '#f8f9fa', border: '2px dashed #6C63FF' }}
                >
                  <Square size={120} className="text-primary mb-3" />
                  <h5>Event QR Code</h5>
                  <p className="text-muted mb-3">Guests scan this to request songs</p>
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    disabled
                  >
                    🎵 Request a Song
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-5" style={{ backgroundColor: '#1C2333' }}>
        <Container>
          <Row className="text-center text-white">
            <Col>
              <h2 className="display-5 fw-bold mb-4">Ready to Get Started?</h2>
              <p className="lead mb-4">
                Join thousands of DJs already using MyWeeshy to connect with their audience
              </p>
              <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                <Link to="/signup" className="text-decoration-none">
                  <Button 
                    size="lg" 
                    style={{ 
                      backgroundColor: '#6C63FF', 
                      borderColor: '#6C63FF',
                      color: 'white'
                    }}
                  >
                    Start for Free
                  </Button>
                </Link>
                <Link to="/login" className="text-decoration-none">
                  <Button 
                    variant="outline-light" 
                    size="lg"
                  >
                    I Have an Account
                  </Button>
                </Link>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default FeaturesPage;